import React from 'react';

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlassDollar } from '@fortawesome/pro-light-svg-icons';

// MUI imports
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { green, red } from '@mui/material/colors';
import { styled } from '@mui/system';

// Utils
import _ from 'lodash';
import { getTotalRates } from '../utils/rates.js';
import { calcSectionOfferSum } from '../../utils/opportunity.js';
import currencyToText from '../../CurrencyToText';

const CustomTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} componentsProps={{ tooltip: { className: className } }} />
))(`
    background-color: #323338;
    box-shadow: 2px 2px 2px rgba(0,0,0,0.3);
`);

function ProfitWidget(props) {
    const { text, sectionOffer, section, sections, products, buyingRates, transportationMethod, exchangeRates, lockedExchangeRates, organization, mode='normal', sx = {} } = props;

    const costs = _.fromPairs(_.map(sectionOffer.fields, field => [field.id, getTotalRates({ field, fieldBuyingRates: field.buyingRates, products, buyingRates, transportationMethod, exchangeRates, organization })]));

    const totalBuyingRate = _.sum(_.values(costs));

    const _sections = _.cloneDeep(sections);
    _sections.forEach(s => {
        s.offers.forEach(o => {
            o.fields.forEach(f => {
                if (f.displayCurrency && f.displayCurrency.convert) {
                    f.displayCurrency = f.displayCurrency?.to || f.displayCurrency;
                } else {
                    delete f.displayCurrency;
                }
            });
        });
    });

    const _sectionOffer = _.cloneDeep(sectionOffer);
    _sectionOffer.fields.forEach(f => {
        if (f.displayCurrency && f.displayCurrency.convert) {
            f.displayCurrency = f.displayCurrency?.to || f.displayCurrency;
        } else {
            delete f.displayCurrency;
        }
    });

    const totalSellingRate = calcSectionOfferSum({ 
        sectionOffer: _sectionOffer, 
        displayCurrency: section.displayCurrency?.to || section.displayCurrency,
        displayCurrencySource: section.displayCurrency?.source || section.displayCurrencySource,
        products, 
        transportationMethod, 
        sections: _sections, 
        exchangeRates, 
        lockedExchangeRates, 
        organization,
        buyingRates
    })?.sum ?? {};

    const defaultCurrency = (_.keys(totalSellingRate) || [organization.defaults?.currency])[0] || organization.defaults?.currency;
    const totalSellingRateCombined = _.sum(_.map(totalSellingRate, (v, k) => v / exchangeRates[k]));

    const profit = (totalSellingRateCombined - totalBuyingRate) * exchangeRates[defaultCurrency];
    const margin = totalSellingRateCombined ? (profit / (totalSellingRateCombined * exchangeRates[defaultCurrency])) * 100 : 0;

    const _green = mode === 'icon-with-hover' ? '#42FF3E' : green[500];
    const _red = mode === 'icon-with-hover' ? '#FF7777' : red[500];

    const result = (
        <>
        <FontAwesomeIcon icon={faMagnifyingGlassDollar} />&nbsp;&nbsp;<span style={{ color: profit >= 0 ? _green : _red }}>{currencyToText(defaultCurrency, true, profit.toLocaleString({ maximumFractionDigits: 2 }))}</span>{profit >= 0 ? <span>&nbsp;(Margin <span style={{ color: profit >= 0 ? _green : _red }}>{margin.toLocaleString({ maximumFractionDigits: 2 })}%)</span></span> : null}
        </>
    );

    if (mode === 'icon-with-hover') {
        return (
            <Box sx={{cursor: 'pointer', ...sx }}>
                <CustomTooltip 
                    title={result}
                    arrow
                >
                    <FontAwesomeIcon icon={faMagnifyingGlassDollar} />
                    &nbsp;
                    <span style={{ color: profit >= 0 ? green[500] : red[500], fontWeight: '800' }}>
                        {margin.toLocaleString({ maximumFractionDigits: 2 })}%
                    </span>
                </CustomTooltip>
            </Box>
        );
    }

    return (
        <Typography variant='caption' component={Box} sx={sx} >
            {result}
        </Typography>
    )
}

export default ProfitWidget;

